import React, { useState, useEffect, useCallback } from 'react';
import { alertboxAPI, funnationAPI, rankingAPI, importAPI } from '../services/api';
import YouTubeConnect from '../components/YouTubeConnect';
import NotificationPanel from '../components/NotificationPanel';
import useNotifications from '../hooks/useNotifications';

function DashboardPage({ user, onLogout, onUserUpdate }) {
  const { notifications, realtimeDonations, activeSpenders, connected: notiConnected, clearNotifications } = useNotifications();
  const [alertboxUrl, setAlertboxUrl] = useState('');
  const [funnationId, setFunnationId] = useState('');
  const [wsStatus, setWsStatus] = useState({
    toonation: { registered: false, connected: false },
    funnation: { registered: false, connected: false },
  });
  const [summary, setSummary] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [period, setPeriod] = useState('all');
  const [toonationLoading, setToonationLoading] = useState(false);
  const [funnationLoading, setFunnationLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedViewer, setSelectedViewer] = useState(null);
  const [importStatus, setImportStatus] = useState({ toonation: null, funnation: null }); // null | 'loading' | 'done' | 'error'
  const [importResults, setImportResults] = useState({ toonation: null, funnation: null });

  // 상태 조회
  const fetchStatus = useCallback(async () => {
    try {
      const res = await alertboxAPI.status();
      setWsStatus({
        toonation: res.data.toonation || { registered: res.data.registered, connected: res.data.connected },
        funnation: res.data.funnation || { registered: false, connected: false },
      });
    } catch (err) {
      console.error('[Dashboard] Status fetch error:', err);
    }
  }, []);

  // 요약 통계
  const fetchSummary = useCallback(async () => {
    try {
      const res = await rankingAPI.summary();
      setSummary(res.data);
    } catch (err) {
      console.error('[Dashboard] Summary fetch error:', err);
    }
  }, []);

  // 랭킹
  const fetchRanking = useCallback(async () => {
    try {
      const res = await rankingAPI.list({ period, limit: 20 });
      setRanking(res.data.ranking);
    } catch (err) {
      console.error('[Dashboard] Ranking fetch error:', err);
    }
  }, [period]);

  useEffect(() => {
    fetchStatus();
    fetchSummary();
    fetchRanking();
    // 30초마다 자동 새로고침
    const interval = setInterval(() => {
      fetchStatus();
      fetchSummary();
      fetchRanking();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchSummary, fetchRanking]);

  // Alertbox URL 등록
  const handleRegisterAlertbox = async () => {
    if (!alertboxUrl.trim()) return;
    setToonationLoading(true);
    setError('');
    try {
      await alertboxAPI.register(alertboxUrl.trim());
      setAlertboxUrl('');
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'URL 등록에 실패했습니다.');
    } finally {
      setToonationLoading(false);
    }
  };

  // 연결 해제
  const handleDisconnect = async () => {
    try {
      await alertboxAPI.disconnect();
      await fetchStatus();
    } catch (err) {
      console.error('[Dashboard] Toonation disconnect error:', err);
    }
  };

  // 펀네이션 등록
  const handleRegisterFunnation = async () => {
    if (!funnationId.trim()) return;
    setFunnationLoading(true);
    setError('');
    try {
      await funnationAPI.register(funnationId.trim());
      setFunnationId('');
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || '펀네이션 연결에 실패했습니다.');
    } finally {
      setFunnationLoading(false);
    }
  };

  // 펀네이션 연결 해제
  const handleDisconnectFunnation = async () => {
    try {
      await funnationAPI.disconnect();
      await fetchStatus();
    } catch (err) {
      console.error('[Dashboard] Funnation disconnect error:', err);
    }
  };

  // 투네이션 과거 데이터 임포트
  const handleImportToonation = async () => {
    setImportStatus(prev => ({ ...prev, toonation: 'loading' }));
    try {
      const res = await importAPI.toonation();
      setImportResults(prev => ({ ...prev, toonation: res.data.results }));
      setImportStatus(prev => ({ ...prev, toonation: 'done' }));
      fetchSummary();
      fetchRanking();
    } catch (err) {
      setImportStatus(prev => ({ ...prev, toonation: 'error' }));
      setError(err.response?.data?.error || '투네이션 데이터 임포트에 실패했습니다.');
    }
  };

  // 펀네이션 과거 데이터 임포트
  const handleImportFunnation = async () => {
    setImportStatus(prev => ({ ...prev, funnation: 'loading' }));
    try {
      const res = await importAPI.funnation();
      setImportResults(prev => ({ ...prev, funnation: res.data.results }));
      setImportStatus(prev => ({ ...prev, funnation: 'done' }));
      fetchSummary();
      fetchRanking();
    } catch (err) {
      setImportStatus(prev => ({ ...prev, funnation: 'error' }));
      setError(err.response?.data?.error || '펀네이션 데이터 임포트에 실패했습니다.');
    }
  };

  // 시청자 상세
  const handleViewerClick = async (viewerId) => {
    try {
      const res = await rankingAPI.viewer(viewerId);
      setSelectedViewer(res.data);
    } catch (err) {
      console.error('[Dashboard] Viewer detail error:', err);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const getTierBadge = (tier) => {
    const labels = { whale: '큰손', potential: '잠재큰손', regular: '단골', normal: '일반' };
    return <span className={`tier-badge tier-${tier || 'normal'}`}>{labels[tier] || '일반'}</span>;
  };

  return (
    <div className="app-container">
      {/* Navigation */}
      <nav className="nav">
        <div className="nav-logo">keunson</div>
        <div className="nav-user">
          <span
            className={`status-dot ${(wsStatus.toonation.connected || wsStatus.funnation.connected) ? 'connected' : 'disconnected'}`}
          />
          <span>{(wsStatus.toonation.connected || wsStatus.funnation.connected) ? '수집 중' : '대기'}</span>
          <span style={{ color: '#8B8B9E' }}>|</span>
          <span>{user.nickname}</span>
          <a href="/chat" className="btn btn-outline" style={{ padding: '6px 14px', fontSize: '0.85rem', textDecoration: 'none' }}>
            실시간 채팅
          </a>
          <button className="btn btn-outline" style={{ padding: '6px 14px', fontSize: '0.85rem' }} onClick={onLogout}>
            로그아웃
          </button>
        </div>
      </nav>

      {/* 플랫폼 연결 (투네이션 + 펀네이션) */}
      {error && <div className="card"><div className="error-msg">{error}</div></div>}
      <div className="stats-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {/* 투네이션 카드 */}
        <div className="card">
          <h2 style={{ marginBottom: 8, fontSize: '1rem' }}>투네이션</h2>
          {!wsStatus.toonation.registered ? (
            <>
              <p style={{ color: '#8B8B9E', marginBottom: 12, fontSize: '0.85rem' }}>
                투네이션 크리에이터 페이지 → 통합 알림창 → 통합 알림 URL을 복사해서 붙여넣으세요.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  className="form-input"
                  value={alertboxUrl}
                  onChange={(e) => setAlertboxUrl(e.target.value)}
                  placeholder="https://toon.at/widget/alertbox/..."
                  style={{ flex: 1, fontSize: '0.85rem' }}
                />
                <button className="btn btn-primary" onClick={handleRegisterAlertbox} disabled={toonationLoading} style={{ fontSize: '0.85rem' }}>
                  {toonationLoading ? '...' : '연결'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span className={`status-dot ${wsStatus.toonation.connected ? 'connected' : 'disconnected'}`} />
                  <strong style={{ fontSize: '0.9rem' }}>{wsStatus.toonation.connected ? '수집 중' : '연결 끊김'}</strong>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="btn btn-outline"
                    style={{ padding: '4px 12px', fontSize: '0.8rem' }}
                    onClick={handleImportToonation}
                    disabled={importStatus.toonation === 'loading'}
                  >
                    {importStatus.toonation === 'loading' ? '가져오는 중...' : '과거 랭킹 가져오기'}
                  </button>
                  <button className="btn btn-danger" style={{ padding: '4px 12px', fontSize: '0.8rem' }} onClick={handleDisconnect}>
                    해제
                  </button>
                </div>
              </div>
              {importStatus.toonation === 'done' && importResults.toonation && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(0,200,100,0.1)', borderRadius: 6, fontSize: '0.8rem', color: '#4ade80' }}>
                  {importResults.toonation.imported}명의 랭킹 데이터를 가져왔습니다.
                </div>
              )}
            </>
          )}
        </div>

        {/* 펀네이션 카드 */}
        <div className="card">
          <h2 style={{ marginBottom: 8, fontSize: '1rem' }}>펀네이션</h2>
          {!wsStatus.funnation.registered ? (
            <>
              <p style={{ color: '#8B8B9E', marginBottom: 12, fontSize: '0.85rem' }}>
                펀네이션 스토어 URL의 슬러그를 입력하세요 (예: ymss).
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  className="form-input"
                  value={funnationId}
                  onChange={(e) => setFunnationId(e.target.value)}
                  placeholder="크리에이터 ID (예: ymss)"
                  style={{ flex: 1, fontSize: '0.85rem' }}
                />
                <button className="btn btn-primary" onClick={handleRegisterFunnation} disabled={funnationLoading} style={{ fontSize: '0.85rem' }}>
                  {funnationLoading ? '...' : '연결'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span className={`status-dot ${wsStatus.funnation.connected ? 'connected' : 'disconnected'}`} />
                  <strong style={{ fontSize: '0.9rem' }}>{wsStatus.funnation.connected ? '수집 중' : '연결 끊김'}</strong>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="btn btn-outline"
                    style={{ padding: '4px 12px', fontSize: '0.8rem' }}
                    onClick={handleImportFunnation}
                    disabled={importStatus.funnation === 'loading'}
                  >
                    {importStatus.funnation === 'loading' ? '가져오는 중...' : '명예의전당 가져오기'}
                  </button>
                  <button className="btn btn-danger" style={{ padding: '4px 12px', fontSize: '0.8rem' }} onClick={handleDisconnectFunnation}>
                    해제
                  </button>
                </div>
              </div>
              {/* 위젯 WS / 폴러 세부 연결 상태 */}
              <div style={{ marginTop: 8, display: 'flex', gap: 12, fontSize: '0.75rem', color: '#8B8B9E' }}>
                <span>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', marginRight: 4, background: wsStatus.funnation.widgetConnected ? '#4ade80' : '#ef4444' }} />
                  실시간 WS {wsStatus.funnation.widgetConnected ? '연결됨' : '끊김'}
                </span>
                <span>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', marginRight: 4, background: wsStatus.funnation.pollerActive ? '#4ade80' : '#ef4444' }} />
                  백업 폴링 {wsStatus.funnation.pollerActive ? '활성' : '비활성'}
                </span>
              </div>
              {importStatus.funnation === 'done' && importResults.funnation && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(0,200,100,0.1)', borderRadius: 6, fontSize: '0.8rem', color: '#4ade80' }}>
                  {importResults.funnation.imported}명의 명예의전당 데이터를 가져왔습니다.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 요약 통계 */}
      {summary && (
        <div className="stats-grid">
          <div className="card">
            <div className="card-title">오늘 후원 총액</div>
            <div className="card-value amount">{formatAmount(summary.today.today_total)}원</div>
            <div style={{ color: '#8B8B9E', fontSize: '0.85rem', marginTop: 4 }}>
              {summary.today.today_count}건 / {summary.today.today_viewers}명
            </div>
          </div>
          <div className="card">
            <div className="card-title">전체 누적 후원</div>
            <div className="card-value amount">{formatAmount(summary.total.total_donated)}원</div>
            <div style={{ color: '#8B8B9E', fontSize: '0.85rem', marginTop: 4 }}>
              총 {summary.total.total_count}건 / {summary.total.total_viewers}명
            </div>
          </div>
          <div className="card">
            <div className="card-title">후원 시청자 수</div>
            <div className="card-value">{summary.total.total_viewers}명</div>
          </div>
        </div>
      )}

      {/* YouTube 연동 + 실시간 알림 */}
      <div className="stats-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <YouTubeConnect user={user} onUserUpdate={onUserUpdate} />
        <NotificationPanel
          notifications={notifications}
          realtimeDonations={realtimeDonations}
          activeSpenders={activeSpenders}
          connected={notiConnected}
          onClear={clearNotifications}
        />
      </div>

      {/* 랭킹 테이블 */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2>후원 랭킹</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {['today', '7d', '30d', '90d', 'all'].map((p) => (
              <button
                key={p}
                className={`btn ${period === p ? 'btn-primary' : 'btn-outline'}`}
                style={{ padding: '6px 14px', fontSize: '0.8rem' }}
                onClick={() => setPeriod(p)}
              >
                {p === 'today' ? '오늘' : p === '7d' ? '7일' : p === '30d' ? '30일' : p === '90d' ? '90일' : '전체'}
              </button>
            ))}
          </div>
        </div>

        {ranking.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#8B8B9E' }}>
            {period === 'today' ? (
              <>
                <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>오늘 후원 내역이 아직 없습니다</div>
                <div style={{ fontSize: '0.85rem' }}>방송 중 후원이 들어오면 실시간으로 여기에 표시됩니다.</div>
              </>
            ) : (wsStatus.toonation.registered || wsStatus.funnation.registered)
              ? '아직 수집된 후원 데이터가 없습니다. 방송을 시작하면 자동으로 집계됩니다.'
              : '먼저 투네이션 또는 펀네이션을 연결해주세요.'}
          </div>
        ) : (
          <table className="ranking-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>순위</th>
                <th>닉네임</th>
                <th style={{ textAlign: 'right' }}>{period === 'today' ? '오늘 후원액' : period === 'all' ? '누적 후원액' : '기간 후원액'}</th>
                {period !== 'all' && <th style={{ textAlign: 'right' }}>누적 후원액</th>}
                <th style={{ textAlign: 'right' }}>횟수</th>
                <th>등급</th>
                <th>마지막 후원</th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((viewer) => (
                <tr
                  key={viewer.viewer_id}
                  onClick={() => handleViewerClick(viewer.viewer_id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <span className={`rank-num ${viewer.rank <= 3 ? `rank-${viewer.rank}` : ''}`}>
                      {viewer.rank}
                    </span>
                  </td>
                  <td><strong>{viewer.nickname}</strong></td>
                  <td style={{ textAlign: 'right' }}>
                    <>
                      <span className={`amount ${viewer.total_donated >= 1000000 ? 'amount-whale' : ''}`}
                            style={viewer.source === 'realtime' ? { color: '#4ade80' } : {}}>
                        {viewer.source === 'realtime' ? '+' : ''}{formatAmount(viewer.total_donated)}원
                      </span>
                      {viewer.source === 'import' && period !== 'all' && (
                        <div style={{ color: '#8B8B9E', fontSize: '0.65rem', marginTop: 2 }}>(누적)</div>
                      )}
                    </>
                  </td>
                  {period !== 'all' && (
                    <td style={{ textAlign: 'right', color: '#8B8B9E', fontSize: '0.85rem' }}>
                      {viewer.cumulative_total ? `${formatAmount(viewer.cumulative_total)}원` : '-'}
                    </td>
                  )}
                  <td style={{ textAlign: 'right' }}>{viewer.donation_count}회</td>
                  <td>
                    {getTierBadge(viewer.tier || 'normal')}
                  </td>
                  <td style={{ color: '#8B8B9E', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {viewer.last_donated_at
                      ? new Date(viewer.last_donated_at).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' })
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 시청자 상세 모달 */}
      {selectedViewer && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setSelectedViewer(null)}
        >
          <div className="card" style={{ maxWidth: 520, width: '90%', maxHeight: '80vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2>{selectedViewer.viewer.nickname}</h2>
              <button className="btn btn-outline" style={{ padding: '4px 12px', fontSize: '0.85rem' }} onClick={() => setSelectedViewer(null)}>
                닫기
              </button>
            </div>

            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <div>
                <div className="card-title">누적 후원</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>
                  {formatAmount(selectedViewer.viewer.total_donated || 0)}원
                </div>
              </div>
              <div>
                <div className="card-title">후원 횟수</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>
                  {selectedViewer.viewer.donation_count || 0}회
                </div>
              </div>
              <div>
                <div className="card-title">등급</div>
                <div>{getTierBadge(selectedViewer.viewer.tier)}</div>
              </div>
            </div>

            <h3 style={{ marginTop: 16, marginBottom: 8, fontSize: '0.95rem' }}>최근 후원 내역</h3>
            {selectedViewer.recent_donations.length > 0 ? (
              <div>
                {selectedViewer.recent_donations.map((d, i) => (
                  <div key={`${d.donated_at}_${d.amount}_${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span className="amount">{formatAmount(d.amount)}원</span>
                    <span style={{ color: '#8B8B9E', fontSize: '0.85rem' }}>
                      {new Date(d.donated_at).toLocaleString('ko-KR')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#8B8B9E' }}>후원 내역이 없습니다.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
